<template>
  <div class="container mx-auto p-4">
    <!-- Header -->
    <div class="mb-6 text-center">
      <h1 class="text-3xl font-bold text-gray-900">All Assigned Jobs</h1>
      <p class="text-gray-600">Showing all jobs linked to your access token.</p>
    </div>

    <!-- Jobs List -->
    <div v-if="isLoading" class="text-center text-gray-500">Loading jobs...</div>
    <div v-else-if="jobs.length" class="space-y-4">
      <div
        v-for="(b, idx) in jobs"
        :key="b.id || idx"
        class="border rounded-lg shadow-sm bg-white overflow-hidden"
      >
        <!-- Header row (click to expand) -->
        <button
          @click="toggleExpand(idx)"
          class="w-full text-left flex justify-between items-center px-6 py-4 bg-gray-50 border-b border-gray-200 focus:outline-none"
        >
          <div>
            <h2 class="text-lg font-semibold text-gray-900">
              {{ b.id }} — {{ b.vesselName || 'No Vessel' }} ({{ b.counterNumber || 'No Counter' }})
            </h2>
            <p class="text-sm text-gray-600">
              {{ getDisplayStatus(b.status) }} • {{ formatDT(b.startTime) }}
            </p>
          </div>
          <span class="flex items-center gap-2">
            <span class="badge" :class="getStatusColor(b.status)">
              {{ getDisplayStatus(b.status) }}
            </span>
            <i
              class="bi"
              :class="expanded[idx] ? 'bi-chevron-up' : 'bi-chevron-down'"
            ></i>
          </span>
        </button>

        <!-- Expandable details -->
        <transition name="fade">
          <div v-if="expanded[idx]" class="px-6 py-4 bg-white space-y-4">

            <!-- Start / Pax -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 class="font-medium text-gray-900 mb-1">Start Time</h4>
                <p class="text-sm text-gray-600">{{ formatDT(b.startTime) }}</p>
              </div>
              <div>
                <h4 class="font-medium text-gray-900 mb-1">Pax</h4>
                <p class="text-sm text-gray-600">{{ b.pax || '—' }}</p>
              </div>
            </div>

            <!-- Locations -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 class="font-medium text-gray-900 mb-1">Start Location</h4>
                <p class="text-sm text-gray-600">{{ b.rawStartLocation || '—' }}</p>
              </div>
              <div>
                <h4 class="font-medium text-gray-900 mb-1">End Location</h4>
                <p class="text-sm text-gray-600">{{ b.rawEndLocation || '—' }}</p>
              </div>
            </div>

            <!-- Vessel / Counter -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 class="font-medium text-gray-900 mb-1">Vessel Name</h4>
                <p class="text-sm text-gray-600">{{ b.vesselName || '—' }}</p>
              </div>
              <div>
                <h4 class="font-medium text-gray-900 mb-1">Counter</h4>
                <p class="text-sm text-gray-600">{{ b.counterNumber || '—' }}</p>
              </div>
            </div>

            <!-- Crew -->
            <div>
              <h4 class="font-medium text-gray-900 mb-1">Crew Name</h4>
              <p class="text-sm text-gray-600">{{ b.crewName || '—' }}</p>
            </div>

            <!-- Trips -->
            <div v-if="b.trips && b.trips.length">
              <h4 class="font-medium text-gray-900 mb-1">Trips</h4>
              <ul class="list-disc pl-5 text-sm text-gray-600">
                <li v-for="(trip,i) in b.trips" :key="i">{{ trip }}</li>
              </ul>
            </div>

            <!-- Drivers -->
            <div v-if="b.driverNames && b.driverNames.length">
              <h4 class="font-medium text-gray-900 mb-1">Assigned Drivers</h4>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="(id,i) in b.driverNames"
                  :key="i"
                  class="badge bg-blue-100 text-blue-800"
                >
                  {{ id }}
                </span>
              </div>
            </div>


            <!-- Remarks -->
            <div>
              <h4 class="font-medium text-gray-900 mb-1">Remarks</h4>
              <p class="text-sm text-gray-600 whitespace-pre-wrap">{{ b.remarks || '—' }}</p>
            </div>

            <!-- Vouchers -->
            <div>
              <h4 class="font-medium text-gray-900 mb-2">Vouchers</h4>
              <div v-if="b.vouchers && b.vouchers.length" class="space-y-2">
                <div
                  v-for="(voucher,i) in b.vouchers"
                  :key="i"
                  class="flex justify-between items-center p-3 bg-gray-50 rounded-lg border"
                >
                  <div class="text-gray-800 font-medium">
                    TransportVoucher-{{ i + 1 }}
                    <span class="text-gray-500">({{ voucher }})</span>
                  </div>

  
                </div>
              </div>
              <p v-else class="text-sm text-gray-500">No vouchers yet</p>
            </div>

            <!-- Status Timeline -->
            <div v-if="b.statusTimeline && b.statusTimeline.length" class="mt-4">
              <h4 class="font-medium text-gray-900 mb-2">Status Timeline</h4>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div
                  v-for="(s,i) in sortStatusTimeline(b.statusTimeline)"
                  :key="i"
                  class="border rounded p-3 bg-gray-50"
                >
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    {{ s.displayStatus }}
                  </label>
                  <p class="text-sm text-gray-600">
                    {{ s.time && s.time !== 0 ? formatDT(s.time) : '—' }}
                  </p>
                </div>
              </div>
            </div>

          </div>
        </transition>

      </div>
    </div>

    <div v-else class="text-center text-gray-500">No jobs found for this token.</div>
  </div>
</template>

<script>
import axios from "axios";
import { useToast } from "vue-toastification";

export default {
  name: "JobList",
  data() {
    return {
      token: this.$route.params.token || "",
      jobs: [],
      expanded: {},
      isLoading: true,
      frontendBase: "amdtransportation.com.sg"
    };
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  methods: {
    toggleExpand(idx) {
      this.expanded[idx] = !this.expanded[idx];
      // make Vue detect reactivity change properly
      this.expanded = { ...this.expanded };
    },
    async fetchJobsByToken() {
      try {
        const res = await axios.get(`${this.$apiURL}/api/bookings/by-token-full/${this.token}`);
        const data = res.data || {};
        this.jobs = data.bookings || [];
      } catch (err) {
        console.error("❌ fetchJobsByToken error:", err);
        this.toast.error("Invalid or expired token.");
      } finally {
        this.isLoading = false;
      }
    },
    getDisplayStatus(status) {
      if (!status) return "—";
      if (status.toLowerCase() === "failure") return "Cancelled";
      if (status.toLowerCase() === "dop") return "Completed";
      return status;
    },
    getStatusColor(status) {
      const lower = (status || "").toLowerCase();
      if (lower === "new") return "bg-blue-100 text-blue-800";
      if (lower === "assigned") return "bg-indigo-100 text-indigo-800";
      if (lower === "ongoing" || lower === "on route") return "bg-yellow-100 text-yellow-800";
      if (lower === "completed" || lower === "dop") return "bg-green-100 text-green-800";
      if (lower === "cancelled" || lower === "failure") return "bg-red-100 text-red-800";
      return "bg-gray-100 text-gray-800";
    },
    formatDT(iso) {
      if (!iso) return "—";
      try {
        const d = new Date(iso);
        return d.toLocaleString("en-SG", { hour12: false });
      } catch (e) {
        return iso;
      }
    },
    async verifyAccessToken(token) {
      try {
        const res = await axios.get(`${this.$apiURL}/api/token/verify`, {
          params: { token }
        })
        if (res.data.ok) {
          this.role = res.data.role || 'driver'
          this.toast.success('Access verified')
          return true
        }
      } catch (err) {
        console.error('❌ verifyAccessToken error:', err)
      }
      return false
    },
    sortStatusTimeline(timeline) {
      if (!Array.isArray(timeline)) return [];

      const order = ["On_Route", "Arrived", "On_Board", "DOP", "Cancelled"];

      const lookup = {};
      timeline.forEach(s => lookup[s.status] = s.time);

      return order.map(status => ({
        status,
        displayStatus: status === "DOP" ? "Completed" : status,
        time: lookup[status] || null
      }));
    },
  },
  async mounted() {
    this.token = this.$route.params.token
    if (!this.token) {
      this.toast.error('Missing access token')
      return
    }

    // ✅ Verify token first
    const ok = await this.verifyAccessToken(this.token)
    if (!ok) {
      this.toast.error('Invalid or expired token')
      return
    }
    await this.fetchJobsByToken();
  },
};
</script>

<style scoped>
.badge {
  display: inline-block;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
}
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
